import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Navigation, Save, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm';

export default function LokasiManager() {
  const { user } = useAuth();
  const mosqueId = user?.mosque_id;

  const [form, setForm] = useState({ latitude: '', longitude: '', address: '' });
  const [original, setOriginal] = useState({ latitude: null, longitude: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    if (!mosqueId) return;
    axios.get(`${API_URL}/api/mosques/${mosqueId}`, cfg)
      .then(({ data }) => {
        setForm({
          latitude: data.latitude ?? '',
          longitude: data.longitude ?? '',
          address: data.address ?? '',
        });
        setOriginal({ latitude: data.latitude, longitude: data.longitude });
      })
      .catch(() => toast.error('Gagal memuat data lokasi'))
      .finally(() => setLoading(false));
  }, [mosqueId]);

  const handleGPS = () => {
    if (!navigator.geolocation) {
      toast.error('Browser tidak mendukung GPS');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(7));
        const lng = parseFloat(pos.coords.longitude.toFixed(7));
        setForm(f => ({ ...f, latitude: lat.toString(), longitude: lng.toString() }));
        toast.success(`Koordinat berhasil diambil: ${lat}, ${lng}`);
        setGpsLoading(false);
      },
      (err) => {
        const msg = err.code === 1 ? 'Izin lokasi ditolak' :
          err.code === 2 ? 'Lokasi tidak tersedia' : 'Waktu permintaan GPS habis';
        toast.error(msg);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const handleSave = async () => {
    const lat = form.latitude !== '' ? parseFloat(form.latitude) : null;
    const lng = form.longitude !== '' ? parseFloat(form.longitude) : null;
    if (lat !== null && (isNaN(lat) || lat < -90 || lat > 90)) {
      return toast.error('Latitude tidak valid (harus antara -90 dan 90)');
    }
    if (lng !== null && (isNaN(lng) || lng < -180 || lng > 180)) {
      return toast.error('Longitude tidak valid (harus antara -180 dan 180)');
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/mosques/${mosqueId}`, {
        latitude: lat,
        longitude: lng,
        address: form.address || undefined,
      }, cfg);
      setOriginal({ latitude: lat, longitude: lng });
      toast.success('Koordinat berhasil disimpan!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan koordinat');
    } finally {
      setSaving(false);
    }
  };

  const mapsUrl = () => {
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lng)) return null;
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  };

  const hasCoords = original.latitude != null && original.longitude != null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      {/* Status koordinat saat ini */}
      <div className={`rounded-2xl p-5 border ${hasCoords ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${hasCoords ? 'bg-emerald-600' : 'bg-amber-500'}`}>
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-stone-800 text-sm">
              {hasCoords ? 'Koordinat Terdaftar' : 'Koordinat Belum Diatur'}
            </p>
            <p className="text-xs text-stone-500">
              {hasCoords
                ? `${original.latitude}, ${original.longitude}`
                : 'Masjid belum memiliki koordinat GPS. Tambahkan agar muncul di peta.'}
            </p>
          </div>
          {hasCoords && mapsUrl() && (
            <a href={mapsUrl()} target="_blank" rel="noopener noreferrer"
              className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-xs text-emerald-700 hover:bg-emerald-50 font-medium">
              <ExternalLink className="w-3 h-3" /> Lihat Peta
            </a>
          )}
        </div>
      </div>

      {/* Form koordinat */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-stone-800 text-sm flex items-center gap-2">
            <Navigation className="w-4 h-4 text-emerald-600" />
            Input Koordinat GPS
          </h3>
          <button
            onClick={handleGPS}
            disabled={gpsLoading}
            data-testid="ambil-lokasi-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-60"
          >
            {gpsLoading
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : <Navigation className="w-3.5 h-3.5" />}
            {gpsLoading ? 'Mengambil...' : 'Ambil Lokasi Saya'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">
              Latitude <span className="text-stone-400">(misal: 0.9168)</span>
            </label>
            <input
              type="number"
              step="any"
              data-testid="latitude-input"
              value={form.latitude}
              onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
              className={cls}
              placeholder="0.000000"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">
              Longitude <span className="text-stone-400">(misal: 104.0305)</span>
            </label>
            <input
              type="number"
              step="any"
              data-testid="longitude-input"
              value={form.longitude}
              onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
              className={cls}
              placeholder="100.000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Alamat Lengkap</label>
          <input
            type="text"
            data-testid="address-input"
            value={form.address}
            onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
            className={cls}
            placeholder="Jl. Masjid Raya No. 1, Kelurahan..."
          />
        </div>

        {/* Map preview jika ada koordinat */}
        {form.latitude && form.longitude && !isNaN(parseFloat(form.latitude)) && !isNaN(parseFloat(form.longitude)) && (
          <div className="rounded-xl overflow-hidden border border-stone-200 h-40 bg-stone-100 relative">
            <iframe
              title="Preview Lokasi"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(form.longitude) - 0.01},${parseFloat(form.latitude) - 0.01},${parseFloat(form.longitude) + 0.01},${parseFloat(form.latitude) + 0.01}&layer=mapnik&marker=${form.latitude},${form.longitude}`}
            />
          </div>
        )}

        <p className="text-xs text-stone-400">
          Tips: Untuk koordinat yang akurat, gunakan tombol "Ambil Lokasi Saya" saat berada di dalam masjid, atau cari di Google Maps dan salin koordinatnya.
        </p>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-lokasi-btn"
          className="flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium text-sm transition-colors disabled:opacity-60"
        >
          {saving
            ? <RefreshCw className="w-4 h-4 animate-spin" />
            : <Save className="w-4 h-4" />}
          {saving ? 'Menyimpan...' : 'Simpan Koordinat'}
        </button>
      </div>
    </div>
  );
}
